// ===========================================
// Save the Dates API
// ===========================================
// Admin API for Save the Dates (event notifications, Phase 5).
// Mirrors events.api.ts conventions: apiClient unwraps the {status,data}
// envelope once, so each call does .then((res) => res.data) to reach payload T.

import { apiClient } from '../services/api';
import type { EventVisibility } from './events.api';

interface ApiResponse<T> {
  status: string;
  data: T;
}

export type SaveTheDateStatus = 'DRAFT' | 'SCHEDULED' | 'SENT' | 'FAILED';
export type SaveTheDateChannel = 'EMAIL' | 'SMS' | 'WHATSAPP';

export interface SaveTheDate {
  id: string;
  schoolId: string;
  eventId: string | null;
  title: string;
  message: string | null;
  eventDate: string | null;
  venueName: string | null;
  visibility: EventVisibility;
  targetFamilies: string[];
  targetClasses: string[];
  targetAgeGroups: string[];
  targetProgramLevels: string[];
  channels: SaveTheDateChannel[];
  scheduledFor: string | null;
  status: SaveTheDateStatus;
  sentAt: string | null;
  lastSentAt: string | null;
  amendedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveTheDateInput {
  title: string;
  message?: string | null;
  eventDate?: string | null;
  venueName?: string | null;
  eventId?: string | null;
  visibility?: EventVisibility;
  targetFamilies?: string[];
  targetClasses?: string[];
  targetAgeGroups?: string[];
  targetProgramLevels?: string[];
  channels?: SaveTheDateChannel[];
  scheduledFor?: string | null;
}

export interface ChannelAvailability {
  available: boolean;
  reason?: string;
}
export type AvailableChannels = Record<SaveTheDateChannel, ChannelAvailability>;

export const saveTheDateApi = {
  list: (filters?: { status?: SaveTheDateStatus; eventId?: string }) =>
    apiClient
      .get<ApiResponse<SaveTheDate[]>>('/admin/save-the-dates', { params: filters })
      .then((res) => res.data),

  get: (id: string) =>
    apiClient
      .get<ApiResponse<SaveTheDate>>(`/admin/save-the-dates/${id}`)
      .then((res) => res.data),

  create: (data: SaveTheDateInput) =>
    apiClient
      .post<ApiResponse<SaveTheDate>>('/admin/save-the-dates', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<SaveTheDateInput>) =>
    apiClient
      .patch<ApiResponse<SaveTheDate>>(`/admin/save-the-dates/${id}`, data)
      .then((res) => res.data),

  remove: (id: string) =>
    apiClient
      .delete<ApiResponse<{ id: string }>>(`/admin/save-the-dates/${id}`)
      .then((res) => res.data),

  schedule: (id: string, scheduledFor: string) =>
    apiClient
      .post<ApiResponse<SaveTheDate>>(`/admin/save-the-dates/${id}/schedule`, { scheduledFor })
      .then((res) => res.data),

  send: (id: string) =>
    apiClient
      .post<ApiResponse<{ sent: boolean }>>(`/admin/save-the-dates/${id}/send`)
      .then((res) => res.data),

  // Resend the same content (not amended).
  resend: (id: string) =>
    apiClient
      .post<ApiResponse<{ lastSentAt: string }>>(`/admin/save-the-dates/${id}/resend`)
      .then((res) => res.data),

  // Apply edits then resend as an Amended Save the Date.
  amend: (id: string, edits?: Partial<SaveTheDateInput>) =>
    apiClient
      .post<ApiResponse<{ amendedCount: number }>>(
        `/admin/save-the-dates/${id}/amend`,
        edits ?? {}
      )
      .then((res) => res.data),

  convertToEvent: (id: string) =>
    apiClient
      .post<ApiResponse<{ eventId: string }>>(`/admin/save-the-dates/${id}/convert-to-event`)
      .then((res) => res.data),

  availableChannels: () =>
    apiClient
      .get<ApiResponse<AvailableChannels>>('/admin/save-the-dates/channels/available')
      .then((res) => res.data),
};
